import type {
    BootstrapCommerceContextResponse,
    CommerceContext,
    CommerceFeature,
    CommerceSession,
} from "@teqtank/commerce-contracts";
import { CommerceError } from "../errors/commerce-error.js";
import { createCommerceFeatureSet } from "./create-commerce-feature-set.js";

const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/u;
const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/u;
const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}$/u;
const LOCALE_CODE_PATTERN = /^[a-z]{2,3}(?:-[A-Z]{2})?$/u;

export function resolveCommerceContext(
    response: BootstrapCommerceContextResponse | null | undefined,
    session: CommerceSession,
): CommerceContext {
    if (response === null || response === undefined || typeof response !== "object") {
        throw createInvalidContextError(session, "The Commerce context response was missing.");
    }

    const tenant = Object.freeze({
        id: requireString(response.tenant?.id, "tenant.id", session),

        name: requireString(response.tenant?.name, "tenant.name", session),
    });

    const storefront = Object.freeze({
        id: requireString(response.storefront?.id, "storefront.id", session),

        alias: requireString(response.storefront?.alias, "storefront.alias", session),

        name: requireString(response.storefront?.name, "storefront.name", session),
    });

    const countryCode = requireString(
        response.market?.countryCode,
        "market.countryCode",
        session,
    ).toUpperCase();

    if (!COUNTRY_CODE_PATTERN.test(countryCode)) {
        throw createInvalidContextError(
            session,
            "Commerce context market.countryCode must be a valid ISO 3166-1 alpha-2 code.",
            {
                property: "market.countryCode",
                value: countryCode,
            },
        );
    }

    const currencyCode = requireString(
        response.currency?.code,
        "currency.code",
        session,
    ).toUpperCase();

    if (!CURRENCY_CODE_PATTERN.test(currencyCode)) {
        throw createInvalidContextError(
            session,
            "Commerce context currency.code must be a valid ISO 4217 currency code.",
            {
                property: "currency.code",
                value: currencyCode,
            },
        );
    }

    const decimalPlaces = response.currency?.decimalPlaces;

    if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0 || decimalPlaces > 4) {
        throw createInvalidContextError(
            session,
            "Commerce context currency.decimalPlaces must be an integer between zero and four.",
            {
                property: "currency.decimalPlaces",
                value: decimalPlaces,
            },
        );
    }

    const localeCode = requireString(response.locale?.code, "locale.code", session);

    if (!LOCALE_CODE_PATTERN.test(localeCode)) {
        throw createInvalidContextError(
            session,
            "Commerce context locale.code must be a supported locale identifier.",
            {
                property: "locale.code",
                value: localeCode,
            },
        );
    }

    const languageCode = requireString(
        response.locale?.languageCode,
        "locale.languageCode",
        session,
    ).toLowerCase();

    if (!LANGUAGE_CODE_PATTERN.test(languageCode)) {
        throw createInvalidContextError(
            session,
            "Commerce context locale.languageCode must be a supported language identifier.",
            {
                property: "locale.languageCode",
                value: languageCode,
            },
        );
    }

    if (!Array.isArray(response.features)) {
        throw createInvalidContextError(session, "Commerce context features must be an array.", {
            property: "features",
        });
    }

    let features;

    try {
        features = createCommerceFeatureSet(response.features as readonly CommerceFeature[]);
    } catch (cause) {
        throw createInvalidContextError(
            session,
            "Commerce context contained an unsupported feature.",
            {
                property: "features",
                cause,
            },
        );
    }

    const countries = resolveCountries(response.countries, session);
    const shippingCountries = resolveShippingCountries(response.shippingCountries, session);
    const selectedCountry = countries.find((country) => country.code === countryCode);
    if (
        selectedCountry === undefined ||
        !selectedCountry.currencies.some((currency) => currency.code === currencyCode)
    ) {
        throw createInvalidContextError(
            session,
            "Commerce context selected a market that is not available.",
        );
    }

    const context: CommerceContext = {
        tenant,

        storefront,

        market: Object.freeze({
            id: requireString(response.market?.id, "market.id", session),

            name: requireString(response.market?.name, "market.name", session),

            countryCode,
        }),

        currency: Object.freeze({
            code: currencyCode,

            symbol: requireString(response.currency?.symbol, "currency.symbol", session),

            decimalPlaces,
        }),

        locale: Object.freeze({
            code: localeCode,
            languageCode,

            timeZone: requireString(response.locale?.timeZone, "locale.timeZone", session),
        }),

        countries,

        shippingCountries,

        features,
    };

    return Object.freeze(context);
}

function resolveShippingCountries(
    value: unknown,
    session: CommerceSession,
): readonly import("@teqtank/commerce-contracts").CommerceShippingCountryOption[] {
    if (!Array.isArray(value) || value.length === 0) {
        throw createInvalidContextError(
            session,
            "Commerce context shippingCountries must be a non-empty array.",
        );
    }

    const seen = new Set<string>();
    return Object.freeze(
        value.map((candidate, index) => {
            const country = candidate as Record<string, unknown>;
            const code = requireString(
                country.code,
                `shippingCountries[${index}].code`,
                session,
            ).toUpperCase();
            if (!COUNTRY_CODE_PATTERN.test(code) || seen.has(code)) {
                throw createInvalidContextError(
                    session,
                    "Commerce context shippingCountries contained an invalid or duplicate country code.",
                );
            }
            seen.add(code);
            return Object.freeze({
                code,
                name: requireString(country.name, `shippingCountries[${index}].name`, session),
            });
        }),
    );
}

function resolveCountries(
    value: unknown,
    session: CommerceSession,
): readonly import("@teqtank/commerce-contracts").CommerceCountryOption[] {
    if (!Array.isArray(value) || value.length === 0) {
        throw createInvalidContextError(
            session,
            "Commerce context countries must be a non-empty array.",
        );
    }

    const seenCountries = new Set<string>();
    return Object.freeze(
        value.map((candidate, countryIndex) => {
            const country = candidate as Record<string, unknown>;
            const code = requireString(
                country.code,
                `countries[${countryIndex}].code`,
                session,
            ).toUpperCase();
            if (!COUNTRY_CODE_PATTERN.test(code) || seenCountries.has(code)) {
                throw createInvalidContextError(
                    session,
                    "Commerce context countries contained an invalid or duplicate country code.",
                );
            }
            seenCountries.add(code);

            if (!Array.isArray(country.currencies) || country.currencies.length === 0) {
                throw createInvalidContextError(
                    session,
                    "Each Commerce country requires at least one currency.",
                );
            }

            const seenCurrencies = new Set<string>();
            const currencies = Object.freeze(
                country.currencies.map((currencyCandidate, currencyIndex) => {
                    const currency = currencyCandidate as Record<string, unknown>;
                    const currencyCode = requireString(
                        currency.code,
                        `countries[${countryIndex}].currencies[${currencyIndex}].code`,
                        session,
                    ).toUpperCase();
                    const decimalPlaces = currency.decimalPlaces;
                    if (
                        !CURRENCY_CODE_PATTERN.test(currencyCode) ||
                        seenCurrencies.has(currencyCode) ||
                        !Number.isInteger(decimalPlaces) ||
                        (decimalPlaces as number) < 0 ||
                        (decimalPlaces as number) > 4
                    ) {
                        throw createInvalidContextError(
                            session,
                            "Commerce context countries contained invalid currency data.",
                        );
                    }
                    seenCurrencies.add(currencyCode);
                    return Object.freeze({
                        code: currencyCode,
                        symbol: requireString(
                            currency.symbol,
                            `countries[${countryIndex}].currencies[${currencyIndex}].symbol`,
                            session,
                        ),
                        decimalPlaces: decimalPlaces as number,
                    });
                }),
            );

            return Object.freeze({
                code,
                name: requireString(country.name, `countries[${countryIndex}].name`, session),
                currencies,
            });
        }),
    );
}

function requireString(value: unknown, property: string, session: CommerceSession): string {
    if (typeof value !== "string" || value.trim().length === 0) {
        throw createInvalidContextError(
            session,
            `Commerce context ${property} was missing or empty.`,
            {
                property,
            },
        );
    }

    return value.trim();
}

function createInvalidContextError(
    session: CommerceSession,
    message: string,
    details: Readonly<Record<string, unknown>> = {},
): CommerceError {
    return new CommerceError("context-invalid", message, {
        commerceKey: session.commerceKey,
        ...details,
    });
}
